import {observable, computed, toJS, action, extendObservable} from 'mobx';

export class PersonalDataForm {

  @observable initialValues = {};
  @observable dirtyValues = {};
  @observable visited = {};
  
  PURPOSES = {
    REGISTRATION: 'REGISTRATION',
    PROFILE_EDIT: 'PROFILE_EDIT',
    GUEST_BOOKING: 'GUEST_BOOKING',
  }
  purpose = this.PURPOSES.PROFILE_EDIT;

  @action.bound setPurpose(purpose) {
    this.purpose = purpose;
  }

  getDefaultValues() {
    return {
      salutation: undefined,
      firstName: undefined,
      lastName: undefined,
      organization: undefined,
      email: undefined,
      phone: undefined,
      mobilePhone: undefined,
      faxPhone: undefined,
      userName: undefined,
      oldPassword: undefined,
      password1: undefined,
      password2: undefined,
      timeZone: undefined,
      language: undefined,
    }
  }

  @computed get values() {
    let values = {...toJS(this.initialValues)};
    for(let dvk in this.dirtyValues) {
      if(this.dirtyValues[dvk] !== undefined) {
        values[dvk] = this.dirtyValues[dvk];
      }
    } 
    return values;
  }

  @computed get isDirty() {
    for(let k in this.initialValues) {
      if(this.dirtyValues[k] !== this.initialValues[k]) return true;
    }
    for(let k in this.dirtyValues) {
      if(this.dirtyValues[k] !== this.initialValues[k]) return true;
    }
    return false;
  }

  @computed get somePasswordFilled() {
    return this.values.oldPassword || this.values.password1 || this.values.password2;
  }

  @computed get validations() {
    const validate = (fieldName) => {
      return this.visited[fieldName] ? this.validator[fieldName](this.values[fieldName]) : [];
    }
    return {
      salutation: validate('salutation'), // Mandatory
      firstName: validate('firstName'), // Mandatory
      lastName: validate('lastName'), // Mandatory
      organization: [],
      email: validate('email'), // Mandatory
      phone: [], //validate('phone'),
      mobilePhone: [], //validate('mobilePhone'),
      faxPhone: [], //validate('faxPhone'),
      userName: (this.purpose === this.PURPOSES.REGISTRATION) ? validate('userName') : [], // Mandatory when registering

      oldPassword: (this.purpose === this.PURPOSES.PROFILE_EDIT && this.somePasswordFilled) ? validate('oldPassword') : [], // Mandatory when changing password
      password1: (this.somePasswordFilled || this.purpose === this.PURPOSES.REGISTRATION) ? validate('password1') : [], // Mandatory when registering or changing password
      password2: (this.somePasswordFilled || this.purpose === this.PURPOSES.REGISTRATION) ? validate('password2') : [], // Mandatory when registering or changing password

      timeZone: [], // (this.purpose !== this.PURPOSES.GUEST_BOOKING) ? validate('timeZone') : [],
      language: [], // (this.purpose !== this.PURPOSES.GUEST_BOOKING) ? validate('language') : [], 
    }
  }  

  @computed get allValid() {
    for(let k in this.validations) {
      if(this.validations[k] && this.validations[k].length > 0) {
        return false;
      }
    }
    return true;
  }

  @action.bound visitAll() {
    for(let k of Object.keys(this.getDefaultValues())) {
      this.setVisited(k);
    }
  }

  @observable readOnlyAll = false;

  @action.bound reset(values) {
    this.visited = this.getDefaultValues();
    if(!values) {
      values = this.getDefaultValues();
    }
    for(let k in this.initialValues) {
      this.initialValues[k] = values[k];
      this.dirtyValues[k] = values[k];
    }
  }

  @action.bound init(values) {
    if(!values) {
      values = this.getDefaultValues();
    }    
    for(let k in this.initialValues) {
      this.initialValues[k] = values[k];
    }    
  }

  @action.bound setVisited(fieldName) {
    this.visited[fieldName] = true;
  }

  constructor(validator) {
    this.validator = validator;
    extendObservable(this.visited, this.getDefaultValues());
    extendObservable(this.initialValues, this.getDefaultValues());
    extendObservable(this.dirtyValues, this.getDefaultValues());
  }
}

export class PersonalAddressForm {
  @observable initialValues = {};
  @observable dirtyValues = {};
  @observable visited = {};

  getDefaultValues() {
    return {  
      street: '',
      addition: '',
      postalCode: '',
      city: '',
      country: '',
    }  
  }

  @computed get anyFilled() {
    for(let k in this.getDefaultValues()) {
      if(this.values[k]) return true;
    }
    return false;
  }

  @computed get allValid() {
    for(let k in this.validations) {
      if(this.validations[k] && this.validations[k].length > 0) {
        return false;
      }
    }
    return true;
  }

  @action.bound visitAll() {
    for(let k of Object.keys(this.getDefaultValues())) {
      this.setVisited(k);
    }
  }

  @computed get isDirty() {
    for(let k in this.initialValues) {
      if(this.dirtyValues[k] !== this.initialValues[k]) return true;
    }
    for(let k in this.dirtyValues) {
      if(this.dirtyValues[k] !== this.initialValues[k]) return true;
    }
    return false;
  }  

  @computed get values() {
    let values = {...toJS(this.initialValues)};
    for(let dvk in this.dirtyValues) {
      if(this.dirtyValues[dvk] !== undefined) {
        values[dvk] = this.dirtyValues[dvk]; 
      }
    } 
    return values;
  }

  @computed get validations() {
    const validate = (fieldName) => {
      return this.visited[fieldName] ? this.validator[fieldName](this.values[fieldName]) : [];
    }
    return {
      street: validate('street'), // Mandatory
      addition: validate('addition'),
      postalCode: validate('postalCode'), // Mandatory
      city: validate('city'), // Mandatory
      country: validate('country'), // Mandatory
    }
  }    

  @observable readOnlyAll = false;

  @action.bound reset(values) {
    this.visited = this.getDefaultValues();
    if(!values) {
      values = this.getDefaultValues();
    }
    for(let k in this.initialValues) {
      this.initialValues[k] = values[k];
      this.dirtyValues[k] = values[k];
    }
  }

  @action.bound init(values) {
    if(!values) {
      values = this.getDefaultValues();
    }    
    for(let k in this.initialValues) {
      this.initialValues[k] = values[k];
    }    
  } 

  @action.bound setVisited(fieldName) {
    this.visited[fieldName] = true;
  }  

  constructor(refAddressTypeId, validator) {
    this.validator = validator;
    this.refAddressTypeId = refAddressTypeId;
    extendObservable(this.visited, this.getDefaultValues());
    extendObservable(this.initialValues, this.getDefaultValues());
    extendObservable(this.dirtyValues, this.getDefaultValues());    
  }

}


// WEBPACK FOOTER //
// ./src/view/PersonalDataForm.js